import React, { useEffect } from "react";
import SpotlightOverlay from "./SpotlightOverlay";
import useSpotlightTargetRect, { TARGET_STATUS } from "./useSpotlightTargetRect";
import { getChapterProgress, getPreviousStepId } from "./tutorialEngine";

const MISSING_TARGET_HINT = "We couldn't find this part of the page, but you can keep going.";

/**
 * Presentational bridge for one active step: tracks the real target element and turns the step
 * definition + tutorial state into card/pill props. All state changes are delegated via callbacks.
 */
const SpotlightRunner = ({ chapter, step, isPreview, isRequirementMet, userData, onNext, onBack, onSkip, onTargetGone }) => {
	const targetState = useSpotlightTargetRect({
		targetId: step.target,
		interactive: Boolean(step.interactive),
		enabled: true,
		resetKey: `${chapter.id}:${step.id}`,
		missingAfterMs: step.missingAfterMs,
	});

	const isTargetMissing = targetState.status === TARGET_STATUS.MISSING || targetState.status === TARGET_STATUS.GONE;

	useEffect(() => {
		if (step.completeWhenTargetGone && isTargetMissing) onTargetGone();
	}, [step.id, isTargetMissing]);

	useEffect(() => {
		if (step.skipIfTargetMissing && targetState.status === TARGET_STATUS.MISSING) onNext();
	}, [step.id, targetState.status]);

	const hasNotAppeared = targetState.status === TARGET_STATUS.SEARCHING || isTargetMissing;
	const actionLabel = typeof step.actionLabel === "function" ? step.actionLabel({ userData }) : step.actionLabel;
	const waitsForUser = Boolean(step.waitFor);
	const skipLabel = step.skipLabel || "Skip step";
	const canProceed = isRequirementMet || isPreview || isTargetMissing;
	const progress = getChapterProgress(chapter, step.id);

	const hint = isTargetMissing
		? MISSING_TARGET_HINT
		: !isRequirementMet && !isPreview
			? step.requires.hint
			: waitsForUser
				? step.instruction
				: undefined;

	const cardProps = {
		step,
		chapterTitle: chapter.title,
		progress,
		isPreview,
		hint,
		canGoBack: step.allowBack !== false && Boolean(getPreviousStepId(chapter, step.id)),
		showPrimary: !waitsForUser || step.optional || isPreview || isTargetMissing,
		primaryLabel: isTargetMissing ? "Continue" : waitsForUser ? skipLabel : actionLabel || "Next",
		isPrimaryDisabled: !canProceed,
		onNext,
		onBack,
		onSkip,
	};

	const pillProps = {
		instruction: step.instruction || step.title,
		onSkip,
		skipStepLabel: skipLabel,
		onSkipStep: isTargetMissing || isPreview || step.optional ? onNext : undefined,
	};

	// A step about a transient or optional element is invisible until that element shows up.
	if ((step.completeWhenTargetGone || step.skipIfTargetMissing) && hasNotAppeared) return null;

	return (
		<SpotlightOverlay
			step={step}
			targetState={targetState}
			presentation={step.silent ? "pill" : "card"}
			isPreview={isPreview}
			cardProps={cardProps}
			pillProps={pillProps}
		/>
	);
};

export default SpotlightRunner;
